import { ChangeDetectionStrategy, Component, ElementRef, forwardRef, input, output, signal, viewChild } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR, ReactiveFormsModule } from '@angular/forms';
import { ZagIcon } from '../icon/icon';

/**
 * Default maximum file size when used as an image upload (1MB).
 */
const DEFAULT_MAX_FILE_SIZE = 1024 * 1024;

/**
 * Default MIME allowlist when used as an image upload.
 */
const DEFAULT_ALLOWED_IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'image/svg+xml'];

/** Default value of the native `accept` attribute when used as an image upload. */
const DEFAULT_ACCEPT_ATTRIBUTE = DEFAULT_ALLOWED_IMAGE_TYPES.join(',');

/**
 * Image upload component that provides file upload functionality.
 * Outputs a base64-encoded data URL string.
 *
 * @example Basic usage with ngModel
 * ```html
 * <zag-image-upload
 *   [(ngModel)]="profilePicture"
 *   [label]="'Upload Photo'"
 * />
 * ```
 *
 * @example With reactive forms
 * ```html
 * <zag-image-upload
 *   formControlName="profilePicture"
 *   [showPreview]="true"
 * />
 * ```
 */
@Component({
  selector: 'zag-image-upload',
  template: `
    <div class="zag-image-upload">
      <!-- Preview (optional) -->
      @if (showPreview() && currentValue()) {
        <div class="zag-image-upload__preview">
          <img [src]="currentValue()" alt="Preview" class="zag-image-upload__preview-image" />
          <button
            type="button"
            class="zag-image-upload__preview-remove"
            [disabled]="disabled()"
            (click)="clearImage()"
            [attr.aria-label]="clearLabel()"
          >
            <span zagIcon="close"></span>
          </button>
        </div>
      }

      <!-- Upload area -->
      <div class="zag-image-upload__upload-area" [class.zag-image-upload__upload-area--full-width]="fullWidth()">
        <input
          #fileInput
          type="file"
          [attr.accept]="acceptAttribute()"
          class="zag-image-upload__file-input"
          (change)="onFileSelected($event)"
          [disabled]="disabled()"
        />
        <button
          type="button"
          class="zag-image-upload__upload-btn"
          [class.zag-image-upload__upload-btn--full-width]="fullWidth()"
          [class.zag-image-upload__upload-btn--with-hint]="showHintInButton()"
          [class.zag-image-upload__upload-btn--drag-over]="isDragOver()"
          [disabled]="disabled() || isProcessing()"
          (click)="triggerFileUpload()"
          (dragenter)="onDragEnter($event)"
          (dragover)="onDragOver($event)"
          (dragleave)="onDragLeave($event)"
          (drop)="onDrop($event)"
        >
          <span
            [zagIcon]="isProcessing() ? 'sync' : 'upload'"
            class="zag-image-upload__upload-icon"
            [class.spinning]="isProcessing()"
          ></span>
          <span class="zag-image-upload__btn-content">
            @if (isProcessing()) {
              {{ processingLabel() }}
            } @else {
              {{ label() }}
            }
            @if (showHintInButton() && !isProcessing()) {
              <span class="zag-image-upload__btn-hint">{{ hint() }}</span>
            }
          </span>
        </button>
        @if (!showHintInButton()) {
          <span class="zag-image-upload__hint">{{ hint() }}</span>
        }
      </div>

      <!-- Error message -->
      @if (uploadError()) {
        <div class="zag-image-upload__error" role="alert">
          <span zagIcon="error" class="zag-image-upload__error-icon"></span>
          {{ uploadError() }}
        </div>
      }
    </div>
  `,
  styleUrl: './image-upload.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [ReactiveFormsModule, ZagIcon],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => ImageUpload),
      multi: true,
    },
  ],
  host: {
    class: 'zag-image-upload-host',
    '[class.zag-image-upload-host--disabled]': 'disabled()',
  },
})
export class ImageUpload implements ControlValueAccessor {
  /** Whether the upload is disabled */
  readonly disabled = input<boolean>(false);

  /** Whether to show the image preview */
  readonly showPreview = input<boolean>(true);

  /** Whether the button should fill the container width */
  readonly fullWidth = input<boolean>(false);

  /** Whether to show the hint inside the button (stacked layout) */
  readonly showHintInButton = input<boolean>(false);

  /** Upload button label */
  readonly label = input<string>('Upload Image');

  /** Processing label shown during file read */
  readonly processingLabel = input<string>('Processing...');

  /** Clear button aria-label */
  readonly clearLabel = input<string>('Remove image');

  /** Hint text below the upload button */
  readonly hint = input<string>('JPEG, PNG, GIF, WebP, or SVG. Max 1MB.');

  /**
   * MIME types the picker accepts. Defaults to the image set so legacy
   * avatar/photo callers keep their behaviour. Override to accept documents
   * (e.g. PDF) — the same list is enforced both client-side and via the
   * native `accept` attribute.
   */
  readonly allowedMimeTypes = input<readonly string[]>(DEFAULT_ALLOWED_IMAGE_TYPES);

  /**
   * Native `accept` attribute for the hidden file input. Defaults to the
   * image MIME list; override when accepting non-image MIME types so the OS
   * file chooser shows the correct entries.
   */
  readonly acceptAttribute = input<string>(DEFAULT_ACCEPT_ATTRIBUTE);

  /**
   * Maximum file size in bytes. Defaults to the 1 MB avatar limit; document
   * uploads override this to the document-MIME limit (25 MB).
   */
  readonly maxFileBytes = input<number>(DEFAULT_MAX_FILE_SIZE);

  /**
   * Whether to read the picked file as a base64 data URL and emit it via the
   * `ControlValueAccessor` (default `true`, matching the avatar flow). Set to
   * `false` for document uploads that only need the raw `File` via the
   * {@link fileSelected} output — avoids loading large blobs into memory as
   * data URLs.
   */
  readonly emitDataUrl = input<boolean>(true);

  /** Error shown when the selected file is not an allowed image type */
  readonly invalidFileTypeError = input<string>('Invalid file type. Please upload a JPEG, PNG, GIF, WebP, or SVG image.');

  /** Error shown when the selected file exceeds the avatar upload limit */
  readonly fileTooLargeError = input<string>('File is too large. Maximum size is 1MB.');

  /** Error shown when the browser fails to read the selected file */
  readonly fileReadFailedError = input<string>('Failed to read file. Please try again.');

  /** Emitted when an error occurs */
  readonly uploadFailed = output<string>();

  /** Emitted when a valid file is selected or cleared (null). Use this to receive the raw File for server-side uploads instead of the base64 value from the ControlValueAccessor. */
  readonly fileSelected = output<File | null>();

  /** Reference to the hidden file input */
  private readonly fileInput = viewChild<ElementRef<HTMLInputElement>>('fileInput');

  /** Current image value (base64 data URL) */
  readonly currentValue = signal<string | null>(null);

  /** Error message for file upload */
  readonly uploadError = signal<string | null>(null);

  /** Whether a file is being processed */
  readonly isProcessing = signal<boolean>(false);

  /** Whether a file is being dragged over the drop zone */
  readonly isDragOver = signal<boolean>(false);

  // ControlValueAccessor callbacks
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  private onChange: (value: string | null) => void = () => {};
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  private onTouched: () => void = () => {};

  writeValue(value: string | null): void {
    this.currentValue.set(value);
  }

  registerOnChange(fn: (value: string | null) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  /** Trigger file input click */
  triggerFileUpload(): void {
    if (this.disabled()) return;
    this.fileInput()?.nativeElement.click();
  }

  /** Handle file selection */
  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];

    if (!file) {
      return;
    }

    // Reset input so the same file can be selected again
    input.value = '';

    this.processFile(file);
  }

  /** Clear the current image */
  clearImage(): void {
    if (this.disabled()) return;
    this.currentValue.set(null);
    this.onChange(null);
    this.onTouched();
    this.uploadError.set(null);
    this.fileSelected.emit(null);
  }

  private processFile(file: File): void {
    const validationError = this.validateFile(file);
    if (validationError) {
      this.setUploadError(validationError);
      return;
    }

    this.uploadError.set(null);
    this.fileSelected.emit(file);

    if (!this.emitDataUrl()) {
      this.onTouched();
      return;
    }

    this.isProcessing.set(true);
    const reader = new FileReader();
    reader.onload = () => {
      const base64 = reader.result;
      if (typeof base64 !== 'string') {
        this.setUploadError(this.fileReadFailedError());
        this.isProcessing.set(false);
        return;
      }

      this.currentValue.set(base64);
      this.onChange(base64);
      this.onTouched();
      this.isProcessing.set(false);
    };
    reader.onerror = () => {
      this.setUploadError(this.fileReadFailedError());
      this.isProcessing.set(false);
    };
    reader.readAsDataURL(file);
  }

  private validateFile(file: File): string | null {
    const allowed = this.allowedMimeTypes();
    if (!allowed.includes(file.type)) {
      return this.invalidFileTypeError();
    }

    if (file.size > this.maxFileBytes()) {
      return this.fileTooLargeError();
    }

    return null;
  }

  private setUploadError(error: string): void {
    this.uploadError.set(error);
    this.uploadFailed.emit(error);
  }

  /** Handle drag enter event */
  onDragEnter(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    if (this.disabled() || this.isProcessing()) return;
    this.isDragOver.set(true);
  }

  /** Handle drag over event */
  onDragOver(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
  }

  /** Handle drag leave event */
  onDragLeave(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    if (this.disabled() || this.isProcessing()) return;
    this.isDragOver.set(false);
  }

  /** Handle drop event */
  onDrop(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragOver.set(false);

    if (this.disabled() || this.isProcessing()) return;

    const file = event.dataTransfer?.files?.[0];
    if (!file) return;

    this.processFile(file);
  }
}
